{
  "version": "0.1.38",
  "packageName": "@classic-homes/charts-core",
  "description": "Core utilities, types, and themes for Classic Theme chart components",
  "subpackages": {
    "main": {
      "importPath": "@classic-homes/charts-core",
      "description": "Main entry point with all exports (themes, types, utilities)",
      "modules": {
        "theme": {
          "description": "ECharts theme generation from design tokens",
          "functions": [
            {
              "name": "generateLightTheme",
              "signature": "() => EChartsTheme",
              "description": "Generate light theme configuration from design tokens"
            },
            {
              "name": "generateDarkTheme",
              "signature": "() => EChartsTheme",
              "description": "Generate dark theme configuration from design tokens"
            },
            {
              "name": "lightTheme",
              "signature": "EChartsTheme",
              "description": "Pre-generated light theme object (singleton)"
            },
            {
              "name": "darkTheme",
              "signature": "EChartsTheme",
              "description": "Pre-generated dark theme object (singleton)"
            },
            {
              "name": "THEME_LIGHT",
              "signature": "'classic-light'",
              "description": "Theme name constant for light theme"
            },
            {
              "name": "THEME_DARK",
              "signature": "'classic-dark'",
              "description": "Theme name constant for dark theme"
            }
          ]
        },
        "format": {
          "description": "Formatting utilities for chart values",
          "functions": [
            {
              "name": "formatNumber",
              "signature": "(value: number, options?: Intl.NumberFormatOptions) => string",
              "description": "Format number with thousand separators",
              "example": "formatNumber(1234567) // '1,234,567'"
            },
            {
              "name": "formatCurrency",
              "signature": "(value: number, currency?: string, locale?: string) => string",
              "description": "Format number as currency (default: USD, en-US)",
              "example": "formatCurrency(1234.56) // '$1,234.56'"
            },
            {
              "name": "formatPercent",
              "signature": "(value: number, decimals?: number, locale?: string) => string",
              "description": "Format number as percentage (value should be 0-1)",
              "example": "formatPercent(0.1234, 1) // '12.3%'"
            },
            {
              "name": "formatDate",
              "signature": "(date: Date | string, options?: Intl.DateTimeFormatOptions, locale?: string) => string",
              "description": "Format date for display"
            },
            {
              "name": "abbreviateNumber",
              "signature": "(value: number, decimals?: number) => string",
              "description": "Abbreviate large numbers to K/M/B/T",
              "example": "abbreviateNumber(1234567) // '1.2M'"
            },
            {
              "name": "createTooltipFormatter",
              "signature": "(options?: { showSeriesName?: boolean; valueFormatter?: (value: number) => string; showPercent?: boolean }) => TooltipFormatter",
              "description": "Create an ECharts-compatible tooltip formatter function"
            }
          ]
        },
        "accessibility": {
          "description": "Accessibility utilities for screen readers",
          "functions": [
            {
              "name": "generateLineChartDescription",
              "signature": "(title: string, data: LineChartData) => string",
              "description": "Generate accessible description with data points, series names, and trend information"
            },
            {
              "name": "generateBarChartDescription",
              "signature": "(title: string, data: BarChartData) => string",
              "description": "Generate accessible description with category count, max/min values and their categories"
            },
            {
              "name": "generatePieChartDescription",
              "signature": "(title: string, data: PieSliceData[], isDonut?: boolean) => string",
              "description": "Generate accessible description with segment count and top 3 segments with percentages"
            },
            {
              "name": "generateScatterChartDescription",
              "signature": "(title: string, data: ScatterChartData) => string",
              "description": "Generate accessible description with total point count and series names"
            },
            {
              "name": "generateDataTable",
              "signature": "(headers: string[], rows: (string | number)[][]) => string",
              "description": "Generate hidden HTML data table for screen readers (uses sr-only class)"
            }
          ]
        }
      }
    },
    "theme": {
      "importPath": "@classic-homes/charts-core/theme",
      "description": "Theme subpath export for ECharts theme generation",
      "modules": {
        "theme": {
          "description": "Theme generation and pre-built themes",
          "functions": [
            {
              "name": "generateLightTheme",
              "signature": "() => EChartsTheme",
              "description": "Generate light theme from design tokens"
            },
            {
              "name": "generateDarkTheme",
              "signature": "() => EChartsTheme",
              "description": "Generate dark theme from design tokens"
            },
            {
              "name": "lightTheme",
              "signature": "EChartsTheme",
              "description": "Pre-generated light theme"
            },
            {
              "name": "darkTheme",
              "signature": "EChartsTheme",
              "description": "Pre-generated dark theme"
            },
            {
              "name": "THEME_LIGHT",
              "signature": "'classic-light'",
              "description": "Theme name constant"
            },
            {
              "name": "THEME_DARK",
              "signature": "'classic-dark'",
              "description": "Theme name constant"
            }
          ]
        }
      }
    },
    "types": {
      "importPath": "@classic-homes/charts-core/types",
      "description": "Types subpath export for TypeScript type definitions",
      "modules": {}
    }
  },
  "types": {
    "props": {
      "description": "Component prop types for all chart components",
      "types": [
        {
          "name": "BaseChartProps",
          "description": "Shared props for all chart components",
          "properties": [
            {
              "name": "title",
              "type": "string",
              "required": true,
              "description": "Chart title - required for accessibility (used as aria-label)"
            },
            {
              "name": "description",
              "type": "string",
              "required": false,
              "description": "Optional description for screen readers"
            },
            {
              "name": "height",
              "type": "number | string",
              "required": false,
              "description": "Chart height in pixels or CSS value"
            },
            {
              "name": "minHeight",
              "type": "number",
              "required": false,
              "description": "Minimum height constraint"
            },
            {
              "name": "maxHeight",
              "type": "number",
              "required": false,
              "description": "Maximum height constraint"
            },
            {
              "name": "loading",
              "type": "boolean",
              "required": false,
              "description": "Show loading skeleton"
            },
            {
              "name": "error",
              "type": "string | null",
              "required": false,
              "description": "Error message to display"
            },
            {
              "name": "emptyMessage",
              "type": "string",
              "required": false,
              "description": "Message shown when data is empty"
            },
            {
              "name": "theme",
              "type": "'light' | 'dark' | 'auto'",
              "required": false,
              "description": "Theme preference"
            },
            {
              "name": "animation",
              "type": "boolean",
              "required": false,
              "description": "Enable/disable animations (respects prefers-reduced-motion)"
            },
            {
              "name": "showLegend",
              "type": "boolean",
              "required": false,
              "description": "Show chart legend"
            },
            {
              "name": "showTooltip",
              "type": "boolean",
              "required": false,
              "description": "Show tooltip on hover"
            },
            {
              "name": "showGrid",
              "type": "boolean",
              "required": false,
              "description": "Show grid lines (where applicable)"
            },
            {
              "name": "class",
              "type": "string",
              "required": false,
              "description": "Custom CSS class"
            }
          ]
        },
        {
          "name": "LineChartProps",
          "description": "Line chart props (extends BaseChartProps)",
          "properties": [
            {
              "name": "data",
              "type": "LineChartData",
              "required": true,
              "description": "Chart data with categories and series"
            },
            {
              "name": "smooth",
              "type": "boolean",
              "required": false,
              "description": "Use smooth curved lines"
            },
            {
              "name": "areaFilled",
              "type": "boolean",
              "required": false,
              "description": "Fill area under the line"
            },
            {
              "name": "showDataPoints",
              "type": "boolean",
              "required": false,
              "description": "Show data points on the line"
            },
            {
              "name": "stacked",
              "type": "boolean",
              "required": false,
              "description": "Stack multiple series"
            },
            {
              "name": "onClick",
              "type": "(params: DataPointEventParams) => void",
              "required": false,
              "description": "Click event handler"
            },
            {
              "name": "onHover",
              "type": "(params: DataPointEventParams) => void",
              "required": false,
              "description": "Hover event handler"
            }
          ]
        },
        {
          "name": "BarChartProps",
          "description": "Bar chart props (extends BaseChartProps)",
          "properties": [
            {
              "name": "data",
              "type": "BarChartData",
              "required": true,
              "description": "Chart data with categories and series"
            },
            {
              "name": "orientation",
              "type": "'vertical' | 'horizontal'",
              "required": false,
              "description": "Bar orientation"
            },
            {
              "name": "stacked",
              "type": "boolean",
              "required": false,
              "description": "Stack multiple series"
            },
            {
              "name": "showValues",
              "type": "boolean",
              "required": false,
              "description": "Show value labels on bars"
            },
            {
              "name": "onClick",
              "type": "(params: DataPointEventParams) => void",
              "required": false,
              "description": "Click event handler"
            }
          ]
        },
        {
          "name": "PieChartProps",
          "description": "Pie chart props (extends BaseChartProps)",
          "properties": [
            {
              "name": "data",
              "type": "PieSliceData[]",
              "required": true,
              "description": "Slice data array"
            },
            {
              "name": "showLabels",
              "type": "boolean",
              "required": false,
              "description": "Show slice labels"
            },
            {
              "name": "labelPosition",
              "type": "'inside' | 'outside'",
              "required": false,
              "description": "Label position"
            },
            {
              "name": "innerRadius",
              "type": "number | string",
              "required": false,
              "description": "Inner radius (0 for pie, percentage for donut)"
            },
            {
              "name": "onClick",
              "type": "(params: SliceEventParams) => void",
              "required": false,
              "description": "Click event handler"
            }
          ]
        },
        {
          "name": "DonutChartProps",
          "description": "Donut chart props (extends PieChartProps)",
          "properties": [
            {
              "name": "innerRadius",
              "type": "number | string",
              "required": false,
              "description": "Inner radius (defaults to '50%')"
            },
            {
              "name": "centerLabel",
              "type": "string",
              "required": false,
              "description": "Center label text"
            },
            {
              "name": "centerValue",
              "type": "string | number",
              "required": false,
              "description": "Center value text"
            }
          ]
        },
        {
          "name": "AreaChartProps",
          "description": "Area chart props (extends BaseChartProps)",
          "properties": [
            {
              "name": "data",
              "type": "AreaChartData",
              "required": true,
              "description": "Chart data with categories and series"
            },
            {
              "name": "smooth",
              "type": "boolean",
              "required": false,
              "description": "Use smooth curved lines"
            },
            {
              "name": "stacked",
              "type": "boolean",
              "required": false,
              "description": "Stack multiple series"
            },
            {
              "name": "gradient",
              "type": "boolean",
              "required": false,
              "description": "Use gradient fill"
            },
            {
              "name": "onClick",
              "type": "(params: DataPointEventParams) => void",
              "required": false,
              "description": "Click event handler"
            }
          ]
        },
        {
          "name": "ScatterChartProps",
          "description": "Scatter chart props (extends BaseChartProps)",
          "properties": [
            {
              "name": "data",
              "type": "ScatterChartData",
              "required": true,
              "description": "Chart data with series of coordinate pairs"
            },
            {
              "name": "showTrendLine",
              "type": "boolean",
              "required": false,
              "description": "Show trend/regression line"
            },
            {
              "name": "symbolSize",
              "type": "number | ((value: number[]) => number)",
              "required": false,
              "description": "Symbol size (fixed or function)"
            },
            {
              "name": "onClick",
              "type": "(params: DataPointEventParams) => void",
              "required": false,
              "description": "Click event handler"
            }
          ]
        },
        {
          "name": "RadarChartProps",
          "description": "Radar chart props (extends BaseChartProps)",
          "properties": [
            {
              "name": "data",
              "type": "RadarChartData",
              "required": true,
              "description": "Chart data with series"
            },
            {
              "name": "indicators",
              "type": "RadarIndicator[]",
              "required": true,
              "description": "Radar indicators/dimensions"
            },
            {
              "name": "shape",
              "type": "'polygon' | 'circle'",
              "required": false,
              "description": "Radar shape"
            }
          ]
        },
        {
          "name": "FunnelChartProps",
          "description": "Funnel chart props (extends BaseChartProps)",
          "properties": [
            {
              "name": "data",
              "type": "FunnelSliceData[]",
              "required": true,
              "description": "Funnel stage data"
            },
            {
              "name": "sort",
              "type": "'ascending' | 'descending' | 'none'",
              "required": false,
              "description": "Sort order"
            },
            {
              "name": "orientation",
              "type": "'vertical' | 'horizontal'",
              "required": false,
              "description": "Funnel orientation"
            },
            {
              "name": "showLabels",
              "type": "boolean",
              "required": false,
              "description": "Show value labels"
            },
            {
              "name": "onClick",
              "type": "(params: SliceEventParams) => void",
              "required": false,
              "description": "Click event handler"
            }
          ]
        },
        {
          "name": "GaugeChartProps",
          "description": "Gauge chart props (extends BaseChartProps)",
          "properties": [
            {
              "name": "value",
              "type": "number",
              "required": true,
              "description": "Current value"
            },
            {
              "name": "min",
              "type": "number",
              "required": false,
              "description": "Minimum value"
            },
            {
              "name": "max",
              "type": "number",
              "required": false,
              "description": "Maximum value"
            },
            {
              "name": "splitNumber",
              "type": "number",
              "required": false,
              "description": "Number of split sections"
            },
            {
              "name": "showProgress",
              "type": "boolean",
              "required": false,
              "description": "Show progress arc"
            },
            {
              "name": "formatValue",
              "type": "(value: number) => string",
              "required": false,
              "description": "Value format function"
            }
          ]
        },
        {
          "name": "HeatmapChartProps",
          "description": "Heatmap chart props (extends BaseChartProps)",
          "properties": [
            {
              "name": "data",
              "type": "HeatmapData",
              "required": true,
              "description": "Heatmap data matrix"
            },
            {
              "name": "xAxis",
              "type": "string[]",
              "required": true,
              "description": "X-axis labels"
            },
            {
              "name": "yAxis",
              "type": "string[]",
              "required": true,
              "description": "Y-axis labels"
            },
            {
              "name": "showValues",
              "type": "boolean",
              "required": false,
              "description": "Show values in cells"
            },
            {
              "name": "onClick",
              "type": "(params: HeatmapEventParams) => void",
              "required": false,
              "description": "Click event handler"
            }
          ]
        },
        {
          "name": "TreemapChartProps",
          "description": "Treemap chart props (extends BaseChartProps)",
          "properties": [
            {
              "name": "data",
              "type": "TreemapNode[]",
              "required": true,
              "description": "Hierarchical data"
            },
            {
              "name": "levels",
              "type": "TreemapLevel[]",
              "required": false,
              "description": "Level configurations"
            },
            {
              "name": "onClick",
              "type": "(params: TreemapEventParams) => void",
              "required": false,
              "description": "Click event handler"
            }
          ]
        },
        {
          "name": "CandlestickChartProps",
          "description": "Candlestick chart props (extends BaseChartProps)",
          "properties": [
            {
              "name": "data",
              "type": "CandlestickData",
              "required": true,
              "description": "OHLC data with dates"
            },
            {
              "name": "showVolume",
              "type": "boolean",
              "required": false,
              "description": "Show volume bars"
            },
            {
              "name": "onClick",
              "type": "(params: CandlestickEventParams) => void",
              "required": false,
              "description": "Click event handler"
            }
          ]
        },
        {
          "name": "SankeyChartProps",
          "description": "Sankey diagram props (extends BaseChartProps)",
          "properties": [
            {
              "name": "data",
              "type": "SankeyData",
              "required": true,
              "description": "Nodes and links data"
            },
            {
              "name": "orient",
              "type": "'horizontal' | 'vertical'",
              "required": false,
              "description": "Flow orientation"
            },
            {
              "name": "onClick",
              "type": "(params: SankeyEventParams) => void",
              "required": false,
              "description": "Click event handler"
            }
          ]
        }
      ]
    },
    "data": {
      "description": "Data structure types for chart data",
      "types": [
        {
          "name": "LineChartData",
          "description": "Data structure for line charts",
          "properties": [
            {
              "name": "categories",
              "type": "string[]",
              "required": true,
              "description": "X-axis category labels"
            },
            {
              "name": "series",
              "type": "LineSeriesData[]",
              "required": true,
              "description": "Data series"
            }
          ]
        },
        {
          "name": "LineSeriesData",
          "description": "Series data for line charts",
          "properties": [
            {
              "name": "name",
              "type": "string",
              "required": true,
              "description": "Series name (shown in legend)"
            },
            {
              "name": "data",
              "type": "number[]",
              "required": true,
              "description": "Data values corresponding to categories"
            },
            {
              "name": "color",
              "type": "string",
              "required": false,
              "description": "Override theme color for this series"
            }
          ]
        },
        {
          "name": "BarChartData",
          "description": "Data structure for bar charts (same as LineChartData)",
          "properties": [
            {
              "name": "categories",
              "type": "string[]",
              "required": true,
              "description": "Category labels"
            },
            {
              "name": "series",
              "type": "BarSeriesData[]",
              "required": true,
              "description": "Data series"
            }
          ]
        },
        {
          "name": "BarSeriesData",
          "description": "Series data for bar charts",
          "properties": [
            {
              "name": "name",
              "type": "string",
              "required": true,
              "description": "Series name (shown in legend)"
            },
            {
              "name": "data",
              "type": "number[]",
              "required": true,
              "description": "Data values corresponding to categories"
            },
            {
              "name": "color",
              "type": "string",
              "required": false,
              "description": "Override theme color for this series"
            }
          ]
        },
        {
          "name": "PieSliceData",
          "description": "Data for pie/donut slices",
          "properties": [
            {
              "name": "name",
              "type": "string",
              "required": true,
              "description": "Slice name (shown in legend/tooltip)"
            },
            {
              "name": "value",
              "type": "number",
              "required": true,
              "description": "Slice value"
            },
            {
              "name": "color",
              "type": "string",
              "required": false,
              "description": "Override theme color"
            }
          ]
        },
        {
          "name": "AreaChartData",
          "description": "Data structure for area charts (same as LineChartData)",
          "properties": [
            {
              "name": "categories",
              "type": "string[]",
              "required": true,
              "description": "X-axis category labels"
            },
            {
              "name": "series",
              "type": "AreaSeriesData[]",
              "required": true,
              "description": "Data series"
            }
          ]
        },
        {
          "name": "AreaSeriesData",
          "description": "Series data for area charts",
          "properties": [
            {
              "name": "name",
              "type": "string",
              "required": true,
              "description": "Series name (shown in legend)"
            },
            {
              "name": "data",
              "type": "number[]",
              "required": true,
              "description": "Data values corresponding to categories"
            },
            {
              "name": "color",
              "type": "string",
              "required": false,
              "description": "Override theme color for this series"
            }
          ]
        },
        {
          "name": "ScatterChartData",
          "description": "Data structure for scatter charts",
          "properties": [
            {
              "name": "series",
              "type": "ScatterSeriesData[]",
              "required": true,
              "description": "Data series"
            }
          ]
        },
        {
          "name": "ScatterSeriesData",
          "description": "Series data for scatter charts",
          "properties": [
            {
              "name": "name",
              "type": "string",
              "required": true,
              "description": "Series name"
            },
            {
              "name": "data",
              "type": "[number, number][]",
              "required": true,
              "description": "Data points as [x, y] coordinate pairs"
            },
            {
              "name": "color",
              "type": "string",
              "required": false,
              "description": "Override theme color"
            }
          ]
        },
        {
          "name": "RadarChartData",
          "description": "Data structure for radar charts",
          "properties": [
            {
              "name": "series",
              "type": "RadarSeriesData[]",
              "required": true,
              "description": "Data series"
            }
          ]
        },
        {
          "name": "RadarSeriesData",
          "description": "Series data for radar charts",
          "properties": [
            {
              "name": "name",
              "type": "string",
              "required": true,
              "description": "Series name (shown in legend)"
            },
            {
              "name": "data",
              "type": "number[]",
              "required": true,
              "description": "Values for each indicator in order"
            },
            {
              "name": "color",
              "type": "string",
              "required": false,
              "description": "Override theme color for this series"
            }
          ]
        },
        {
          "name": "RadarIndicator",
          "description": "Radar chart indicator/dimension",
          "properties": [
            {
              "name": "name",
              "type": "string",
              "required": true,
              "description": "Indicator name"
            },
            {
              "name": "max",
              "type": "number",
              "required": true,
              "description": "Maximum value for this indicator"
            },
            {
              "name": "min",
              "type": "number",
              "required": false,
              "description": "Minimum value (defaults to 0)"
            }
          ]
        },
        {
          "name": "FunnelSliceData",
          "description": "Data for funnel stages (same as PieSliceData)",
          "properties": [
            {
              "name": "name",
              "type": "string",
              "required": true,
              "description": "Stage name"
            },
            {
              "name": "value",
              "type": "number",
              "required": true,
              "description": "Stage value"
            },
            {
              "name": "color",
              "type": "string",
              "required": false,
              "description": "Override theme color"
            }
          ]
        },
        {
          "name": "HeatmapData",
          "description": "Data structure for heatmap charts",
          "properties": [
            {
              "name": "data",
              "type": "[number, number, number][]",
              "required": true,
              "description": "Data points as [xIndex, yIndex, value]"
            },
            {
              "name": "min",
              "type": "number",
              "required": false,
              "description": "Minimum value for color scale"
            },
            {
              "name": "max",
              "type": "number",
              "required": false,
              "description": "Maximum value for color scale"
            }
          ]
        },
        {
          "name": "TreemapNode",
          "description": "Hierarchical node for treemap",
          "properties": [
            {
              "name": "name",
              "type": "string",
              "required": true,
              "description": "Node name"
            },
            {
              "name": "value",
              "type": "number",
              "required": false,
              "description": "Node value (leaf nodes)"
            },
            {
              "name": "children",
              "type": "TreemapNode[]",
              "required": false,
              "description": "Child nodes"
            },
            {
              "name": "color",
              "type": "string",
              "required": false,
              "description": "Override theme color"
            }
          ]
        },
        {
          "name": "TreemapLevel",
          "description": "Level configuration for treemap",
          "properties": [
            {
              "name": "depth",
              "type": "number",
              "required": false,
              "description": "Level index (0 = root)"
            },
            {
              "name": "itemStyle",
              "type": "{ borderColor?: string; borderWidth?: number; gapWidth?: number }",
              "required": false,
              "description": "Item style for this level"
            },
            {
              "name": "label",
              "type": "{ show?: boolean; position?: 'inside' | 'outside' }",
              "required": false,
              "description": "Label style for this level"
            }
          ]
        },
        {
          "name": "CandlestickData",
          "description": "OHLC data structure for candlestick charts",
          "properties": [
            {
              "name": "dates",
              "type": "string[]",
              "required": true,
              "description": "Date/time labels for x-axis"
            },
            {
              "name": "data",
              "type": "CandlestickItem[]",
              "required": true,
              "description": "OHLC data items"
            },
            {
              "name": "volume",
              "type": "number[]",
              "required": false,
              "description": "Volume data (optional)"
            }
          ]
        },
        {
          "name": "CandlestickItem",
          "description": "Single candlestick OHLC item",
          "properties": [
            {
              "name": "open",
              "type": "number",
              "required": true,
              "description": "Opening price"
            },
            {
              "name": "close",
              "type": "number",
              "required": true,
              "description": "Closing price"
            },
            {
              "name": "high",
              "type": "number",
              "required": true,
              "description": "Highest price"
            },
            {
              "name": "low",
              "type": "number",
              "required": true,
              "description": "Lowest price"
            }
          ]
        },
        {
          "name": "SankeyData",
          "description": "Data structure for sankey diagrams",
          "properties": [
            {
              "name": "nodes",
              "type": "SankeyNode[]",
              "required": true,
              "description": "Nodes in the diagram"
            },
            {
              "name": "links",
              "type": "SankeyLink[]",
              "required": true,
              "description": "Links between nodes"
            }
          ]
        },
        {
          "name": "SankeyNode",
          "description": "Node in a sankey diagram",
          "properties": [
            {
              "name": "name",
              "type": "string",
              "required": true,
              "description": "Unique node name/id"
            },
            {
              "name": "color",
              "type": "string",
              "required": false,
              "description": "Override theme color"
            }
          ]
        },
        {
          "name": "SankeyLink",
          "description": "Link between sankey nodes",
          "properties": [
            {
              "name": "source",
              "type": "string",
              "required": true,
              "description": "Source node name"
            },
            {
              "name": "target",
              "type": "string",
              "required": true,
              "description": "Target node name"
            },
            {
              "name": "value",
              "type": "number",
              "required": true,
              "description": "Flow value"
            }
          ]
        }
      ]
    },
    "events": {
      "description": "Event parameter types for chart interactions",
      "types": [
        {
          "name": "BaseEventParams",
          "description": "Base event parameters shared across all events",
          "properties": [
            {
              "name": "type",
              "type": "string",
              "required": true,
              "description": "Event type"
            },
            {
              "name": "componentType",
              "type": "string",
              "required": true,
              "description": "Component type that triggered the event"
            },
            {
              "name": "seriesIndex",
              "type": "number",
              "required": false,
              "description": "Series index"
            },
            {
              "name": "seriesName",
              "type": "string",
              "required": false,
              "description": "Series name"
            },
            {
              "name": "dataIndex",
              "type": "number",
              "required": false,
              "description": "Data index within the series"
            },
            {
              "name": "event",
              "type": "MouseEvent",
              "required": false,
              "description": "Original event object"
            }
          ]
        },
        {
          "name": "DataPointEventParams",
          "description": "Event params for line, bar, area, scatter clicks/hovers",
          "properties": [
            {
              "name": "name",
              "type": "string",
              "required": true,
              "description": "Category/x-axis value"
            },
            {
              "name": "value",
              "type": "number | number[]",
              "required": true,
              "description": "Data value"
            },
            {
              "name": "seriesName",
              "type": "string",
              "required": true,
              "description": "Series name"
            },
            {
              "name": "dataIndex",
              "type": "number",
              "required": true,
              "description": "Data index"
            },
            {
              "name": "color",
              "type": "string",
              "required": false,
              "description": "Color of the data point"
            }
          ]
        },
        {
          "name": "SliceEventParams",
          "description": "Event params for pie, donut, funnel clicks",
          "properties": [
            {
              "name": "name",
              "type": "string",
              "required": true,
              "description": "Slice name"
            },
            {
              "name": "value",
              "type": "number",
              "required": true,
              "description": "Slice value"
            },
            {
              "name": "percent",
              "type": "number",
              "required": false,
              "description": "Percentage of total"
            },
            {
              "name": "color",
              "type": "string",
              "required": false,
              "description": "Color of the slice"
            }
          ]
        },
        {
          "name": "HeatmapEventParams",
          "description": "Event params for heatmap cell clicks",
          "properties": [
            {
              "name": "xIndex",
              "type": "number",
              "required": true,
              "description": "X-axis index"
            },
            {
              "name": "yIndex",
              "type": "number",
              "required": true,
              "description": "Y-axis index"
            },
            {
              "name": "xLabel",
              "type": "string",
              "required": true,
              "description": "X-axis label"
            },
            {
              "name": "yLabel",
              "type": "string",
              "required": true,
              "description": "Y-axis label"
            },
            {
              "name": "value",
              "type": "number",
              "required": true,
              "description": "Cell value"
            }
          ]
        },
        {
          "name": "TreemapEventParams",
          "description": "Event params for treemap node clicks",
          "properties": [
            {
              "name": "name",
              "type": "string",
              "required": true,
              "description": "Node name"
            },
            {
              "name": "value",
              "type": "number",
              "required": true,
              "description": "Node value"
            },
            {
              "name": "path",
              "type": "string[]",
              "required": true,
              "description": "Full path from root to this node"
            },
            {
              "name": "depth",
              "type": "number",
              "required": true,
              "description": "Node depth level"
            }
          ]
        },
        {
          "name": "CandlestickEventParams",
          "description": "Event params for candlestick clicks",
          "properties": [
            {
              "name": "date",
              "type": "string",
              "required": true,
              "description": "Date/time label"
            },
            {
              "name": "open",
              "type": "number",
              "required": true,
              "description": "Opening price"
            },
            {
              "name": "close",
              "type": "number",
              "required": true,
              "description": "Closing price"
            },
            {
              "name": "high",
              "type": "number",
              "required": true,
              "description": "Highest price"
            },
            {
              "name": "low",
              "type": "number",
              "required": true,
              "description": "Lowest price"
            },
            {
              "name": "volume",
              "type": "number",
              "required": false,
              "description": "Volume (if available)"
            }
          ]
        },
        {
          "name": "SankeyEventParams",
          "description": "Event params for sankey diagram clicks",
          "properties": [
            {
              "name": "dataType",
              "type": "'node' | 'edge'",
              "required": true,
              "description": "Interaction type"
            },
            {
              "name": "name",
              "type": "string",
              "required": true,
              "description": "For nodes: node name. For edges: source-target string"
            },
            {
              "name": "value",
              "type": "number",
              "required": false,
              "description": "For edges: flow value"
            },
            {
              "name": "source",
              "type": "string",
              "required": false,
              "description": "For edges: source node name"
            },
            {
              "name": "target",
              "type": "string",
              "required": false,
              "description": "For edges: target node name"
            }
          ]
        }
      ]
    },
    "theme": {
      "description": "Theme type definitions",
      "types": [
        {
          "name": "EChartsTheme",
          "description": "Complete ECharts theme configuration type",
          "properties": [
            {
              "name": "color",
              "type": "string[]",
              "required": true,
              "description": "Chart color palette (10 colors)"
            },
            {
              "name": "backgroundColor",
              "type": "string",
              "required": true,
              "description": "Chart background color"
            },
            {
              "name": "textStyle",
              "type": "{ fontFamily: string; color: string }",
              "required": true,
              "description": "Default text style"
            },
            {
              "name": "title",
              "type": "{ textStyle: {...}; subtextStyle: {...} }",
              "required": true,
              "description": "Title styling"
            },
            {
              "name": "legend",
              "type": "{ textStyle: { color: string } }",
              "required": true,
              "description": "Legend styling"
            },
            {
              "name": "tooltip",
              "type": "{ backgroundColor, borderColor, borderWidth, borderRadius, textStyle, extraCssText }",
              "required": true,
              "description": "Tooltip styling"
            },
            {
              "name": "xAxis",
              "type": "{ axisLine, axisTick, axisLabel, splitLine }",
              "required": true,
              "description": "X-axis styling"
            },
            {
              "name": "yAxis",
              "type": "{ axisLine, axisTick, axisLabel, splitLine }",
              "required": true,
              "description": "Y-axis styling"
            },
            {
              "name": "line",
              "type": "{ smooth, symbol, symbolSize }",
              "required": true,
              "description": "Line chart defaults"
            },
            {
              "name": "bar",
              "type": "{ barMaxWidth, itemStyle: { borderRadius } }",
              "required": true,
              "description": "Bar chart defaults"
            },
            {
              "name": "pie",
              "type": "{ itemStyle: { borderColor, borderWidth } }",
              "required": true,
              "description": "Pie chart defaults"
            },
            {
              "name": "scatter",
              "type": "{ symbolSize }",
              "required": true,
              "description": "Scatter chart defaults"
            },
            {
              "name": "gauge",
              "type": "{ axisLine: { lineStyle: { color } } }",
              "required": true,
              "description": "Gauge chart defaults with gradient"
            }
          ]
        }
      ]
    }
  },
  "themeDetails": {
    "description": "Theme color mappings from design tokens",
    "lightTheme": {
      "chartColors": [
        {
          "index": 1,
          "token": "teal[500]",
          "description": "Primary chart color"
        },
        {
          "index": 2,
          "token": "navy[700]",
          "description": "Secondary chart color"
        },
        {
          "index": 3,
          "token": "plum[700]",
          "description": "Tertiary chart color"
        },
        {
          "index": 4,
          "token": "forest[500]",
          "description": "Fourth chart color"
        },
        {
          "index": 5,
          "token": "sky[600]",
          "description": "Fifth chart color"
        },
        {
          "index": 6,
          "token": "stone[600]",
          "description": "Extended palette"
        },
        {
          "index": 7,
          "token": "lavender[700]",
          "description": "Extended palette"
        },
        {
          "index": 8,
          "token": "deepTeal[600]",
          "description": "Extended palette"
        },
        {
          "index": 9,
          "token": "sage[600]",
          "description": "Extended palette"
        },
        {
          "index": 10,
          "token": "red[600]",
          "description": "Extended palette"
        }
      ],
      "semanticMappings": {
        "text": "semanticColors.light.foreground",
        "mutedText": "semanticColors.light.mutedForeground",
        "tooltip": "semanticColors.light.popover",
        "tooltipBorder": "semanticColors.light.border",
        "gridLines": "colors.gray[200]"
      }
    },
    "darkTheme": {
      "chartColors": [
        {
          "index": 1,
          "token": "teal[400]",
          "description": "Primary chart color (lighter)"
        },
        {
          "index": 2,
          "token": "navy[400]",
          "description": "Secondary chart color (lighter)"
        },
        {
          "index": 3,
          "token": "plum[400]",
          "description": "Tertiary chart color (lighter)"
        },
        {
          "index": 4,
          "token": "forest[400]",
          "description": "Fourth chart color (lighter)"
        },
        {
          "index": 5,
          "token": "sky[400]",
          "description": "Fifth chart color (lighter)"
        },
        {
          "index": 6,
          "token": "stone[400]",
          "description": "Extended palette"
        },
        {
          "index": 7,
          "token": "lavender[400]",
          "description": "Extended palette"
        },
        {
          "index": 8,
          "token": "deepTeal[400]",
          "description": "Extended palette"
        },
        {
          "index": 9,
          "token": "sage[400]",
          "description": "Extended palette"
        },
        {
          "index": 10,
          "token": "red[400]",
          "description": "Extended palette"
        }
      ],
      "semanticMappings": {
        "text": "semanticColors.dark.foreground",
        "mutedText": "semanticColors.dark.mutedForeground",
        "tooltip": "semanticColors.dark.popover",
        "tooltipBorder": "semanticColors.dark.border",
        "gridLines": "colors.gray[700]"
      }
    }
  },
  "quickReference": {
    "installation": "npm install @classic-homes/charts-core echarts",
    "basicUsage": "import { lightTheme, darkTheme, formatNumber, type LineChartData } from '@classic-homes/charts-core';",
    "themeSubpath": "import { generateLightTheme, THEME_LIGHT } from '@classic-homes/charts-core/theme';",
    "typesSubpath": "import type { AreaChartProps, AreaChartData } from '@classic-homes/charts-core/types';",
    "peerDependency": "echarts@^5.5.0"
  }
}